// Story 11.6 — ErrorShake context.
// Lifts the shake target up to the app-shell <main> so that 20+ error-toast
// call sites can trigger the micro-shake without individual ref-drilling.
// `useGlobalErrorShake()` returns `shake()` which is a no-op when no provider
// is mounted (e.g. outside the shell, in the auth layout) — this keeps the
// primitive opt-in for flows that live outside the main shell.

import { createContext, useContext, type JSX, type ReactNode } from 'react';
import { useErrorShake } from './error-shake.js';

interface ErrorShakeContextValue {
  shake: () => void;
}

const noop: () => void = () => undefined;

const ErrorShakeContext = createContext<ErrorShakeContextValue>({ shake: noop });

export interface ErrorShakeProviderProps {
  children: (attach: (el: HTMLElement | null) => void) => ReactNode;
}

/**
 * Renders children via a render-prop so the caller can attach the shake ref
 * to any DOM element they choose (typically the shell's `<main>`). The value
 * passed into `children` is a callback ref — assign it to the target's `ref`
 * attribute.
 */
export function ErrorShakeProvider({ children }: ErrorShakeProviderProps): JSX.Element {
  const { ref, shake } = useErrorShake();
  const attach = (el: HTMLElement | null): void => {
    ref.current = el;
  };
  return (
    <ErrorShakeContext.Provider value={{ shake }}>{children(attach)}</ErrorShakeContext.Provider>
  );
}

export function useGlobalErrorShake(): () => void {
  return useContext(ErrorShakeContext).shake;
}
